import { textsApi, TextInstance } from './api';

/**
 * Manages text instances for a map
 * Handles loading, saving, and manipulating texts with region-based storage
 */
export class TextManager {
  private mapId: string;
  private texts: Map<string, TextInstance> = new Map();
  private loadedRegions: Set<string> = new Set();
  private dirtyTexts: Set<string> = new Set();
  private newTexts: Set<string> = new Set();
  private deletedTexts: Set<string> = new Set();
  private saveTimeout: NodeJS.Timeout | null = null;
  private onTextsChanged: (() => void) | null = null;
  private onSaveStateChanged: ((isSaving: boolean, hasUnsaved: boolean) => void) | null = null;
  private onBeforeSave: (() => Promise<void>) | null = null;
  private isSaving: boolean = false;
  private autoSaveEnabled: boolean = true;
  private nextLocalId: number = 1;

  private static readonly SAVE_DELAY = 2000;
  private static readonly REGION_SIZE = 2048;

  constructor(mapId: string) {
    this.mapId = mapId;
  }

  /**
   * Set callback for when texts change
   */
  setOnTextsChanged(callback: (() => void) | null): void {
    this.onTextsChanged = callback;
  }

  /**
   * Set callback for when save state changes
   */
  setOnSaveStateChanged(callback: ((isSaving: boolean, hasUnsaved: boolean) => void) | null): void {
    this.onSaveStateChanged = callback;
  }

  /**
   * Set callback to run before saving (e.g., to save layers first)
   */
  setOnBeforeSave(callback: (() => Promise<void>) | null): void {
    this.onBeforeSave = callback;
  }

  /**
   * Enable or disable autosave
   */
  setAutoSaveEnabled(enabled: boolean): void {
    this.autoSaveEnabled = enabled;
    if (!enabled && this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  }

  /**
   * Notify save state changed
   */
  private notifySaveStateChanged(): void {
    const hasUnsaved = this.dirtyTexts.size > 0 || this.newTexts.size > 0 || this.deletedTexts.size > 0;
    this.onSaveStateChanged?.(this.isSaving, hasUnsaved);
  }

  /**
   * Load all texts for the map
   */
  async loadAllTexts(): Promise<void> {
    try {
      const response = await textsApi.list(this.mapId);
      this.texts.clear();

      for (const text of response.texts) {
        this.texts.set(text.id, text);
      }

      this.notifyTextsChanged();
    } catch (err) {
      console.error('Failed to load texts:', err);
    }
  }

  /**
   * Load texts for a specific region
   */
  async loadRegionTexts(regionX: number, regionY: number): Promise<void> {
    const key = `${regionX},${regionY}`;
    if (this.loadedRegions.has(key)) return;

    try {
      const response = await textsApi.getRegionTexts(this.mapId, regionX, regionY);
      this.loadedRegions.add(key);

      for (const text of response.texts) {
        this.texts.set(text.id, { ...text, regionX, regionY });
      }

      this.notifyTextsChanged();
    } catch (err) {
      console.error(`Failed to load texts for region ${regionX},${regionY}:`, err);
    }
  }

  /**
   * Ensure texts are loaded for visible regions
   */
  async ensureRegionsLoaded(
    viewportX: number,
    viewportY: number,
    viewportWidth: number,
    viewportHeight: number,
    zoom: number
  ): Promise<void> {
    const halfWidth = viewportWidth / (2 * zoom);
    const halfHeight = viewportHeight / (2 * zoom);

    const minX = viewportX - halfWidth;
    const maxX = viewportX + halfWidth;
    const minY = viewportY - halfHeight;
    const maxY = viewportY + halfHeight;

    let minRegionX = Math.floor(minX / TextManager.REGION_SIZE);
    let maxRegionX = Math.floor(maxX / TextManager.REGION_SIZE);
    let minRegionY = Math.floor(minY / TextManager.REGION_SIZE);
    let maxRegionY = Math.floor(maxY / TextManager.REGION_SIZE);

    const MAX_REGIONS_PER_AXIS = 10;
    const regionsX = maxRegionX - minRegionX + 1;
    const regionsY = maxRegionY - minRegionY + 1;

    if (regionsX > MAX_REGIONS_PER_AXIS) {
      const centerRegionX = Math.floor(viewportX / TextManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionX = centerRegionX - halfRegions;
      maxRegionX = centerRegionX + halfRegions;
    }

    if (regionsY > MAX_REGIONS_PER_AXIS) {
      const centerRegionY = Math.floor(viewportY / TextManager.REGION_SIZE);
      const halfRegions = Math.floor(MAX_REGIONS_PER_AXIS / 2);
      minRegionY = centerRegionY - halfRegions;
      maxRegionY = centerRegionY + halfRegions;
    }

    const loadPromises: Promise<void>[] = [];

    for (let rx = minRegionX; rx <= maxRegionX; rx++) {
      for (let ry = minRegionY; ry <= maxRegionY; ry++) {
        loadPromises.push(this.loadRegionTexts(rx, ry));
      }
    }

    await Promise.all(loadPromises);
  }

  /**
   * Get all texts sorted by zIndex
   */
  getTexts(): TextInstance[] {
    return Array.from(this.texts.values()).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
  }

  /**
   * Get a single text by ID
   */
  getText(textId: string): TextInstance | undefined {
    return this.texts.get(textId);
  }

  /**
   * Add a new text locally
   */
  addText(textData: {
    content: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: number | string;
    color?: string;
    alignment?: 'left' | 'center' | 'right';
    strokeColor?: string | null;
    strokeWidth?: number;
    fixedSize?: boolean;
    layerId?: string;
  }): TextInstance {
    const localId = `local_text_${Date.now()}_${this.nextLocalId++}`;

    const regionX = Math.floor(textData.x / TextManager.REGION_SIZE);
    const regionY = Math.floor(textData.y / TextManager.REGION_SIZE);

    const text: TextInstance = {
      id: localId,
      content: textData.content,
      x: textData.x,
      y: textData.y,
      width: textData.width,
      height: textData.height,
      rotation: textData.rotation ?? 0,
      fontFamily: textData.fontFamily ?? 'Arial',
      fontSize: textData.fontSize ?? 24,
      fontWeight: textData.fontWeight ?? 400,
      color: textData.color ?? '#000000',
      alignment: textData.alignment ?? 'left',
      strokeColor: textData.strokeColor ?? null,
      strokeWidth: textData.strokeWidth ?? 0,
      fixedSize: textData.fixedSize ?? false,
      zIndex: this.getMaxZIndex() + 1,
      layerId: textData.layerId ?? 'default',
      regionX,
      regionY
    };

    this.texts.set(localId, text);
    this.newTexts.add(localId);
    this.scheduleSave();
    this.notifyTextsChanged();
    this.notifySaveStateChanged();

    return text;
  }

  /**
   * Update a text locally (with debounced save)
   */
  updateTextLocal(textId: string, updates: Partial<TextInstance>): void {
    const text = this.texts.get(textId);
    if (!text) return;

    Object.assign(text, updates);

    // Recalculate region if position changed
    if (updates.x !== undefined || updates.y !== undefined) {
      text.regionX = Math.floor(text.x / TextManager.REGION_SIZE);
      text.regionY = Math.floor(text.y / TextManager.REGION_SIZE);
    }

    this.dirtyTexts.add(textId);
    this.scheduleSave();
    this.notifyTextsChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Delete a text locally
   */
  deleteText(textId: string): boolean {
    if (!this.texts.has(textId)) return false;

    this.texts.delete(textId);
    this.dirtyTexts.delete(textId);

    if (this.newTexts.has(textId)) {
      this.newTexts.delete(textId);
    } else {
      this.deletedTexts.add(textId);
    }

    this.scheduleSave();
    this.notifyTextsChanged();
    this.notifySaveStateChanged();

    return true;
  }

  /**
   * Move texts to a different layer
   */
  moveTextsToLayer(textIds: string[], targetLayerId: string): void {
    for (const textId of textIds) {
      const text = this.texts.get(textId);
      if (text) {
        text.layerId = targetLayerId;
        this.dirtyTexts.add(textId);
      }
    }
    this.scheduleSave();
    this.notifyTextsChanged();
    this.notifySaveStateChanged();
  }

  /**
   * Get texts on a specific layer
   */
  getTextsOnLayer(layerId: string): TextInstance[] {
    return this.getTexts().filter(t => (t.layerId || 'default') === layerId);
  }

  /**
   * Delete all texts on specified layers
   */
  deleteTextsOnLayers(layerIds: string[]): void {
    const layerIdSet = new Set(layerIds);
    const textsToDelete: string[] = [];

    for (const text of this.texts.values()) {
      const textLayerId = text.layerId || 'default';
      if (layerIdSet.has(textLayerId)) {
        textsToDelete.push(text.id);
      }
    }

    for (const textId of textsToDelete) {
      this.texts.delete(textId);
      this.dirtyTexts.delete(textId);

      if (this.newTexts.has(textId)) {
        this.newTexts.delete(textId);
      } else {
        this.deletedTexts.add(textId);
      }
    }

    if (textsToDelete.length > 0) {
      this.scheduleSave();
      this.notifyTextsChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Find text at a given world position
   */
  findTextAtPosition(worldX: number, worldY: number, tolerance: number = 10): TextInstance | null {
    const sortedTexts = this.getTexts().reverse();

    for (const text of sortedTexts) {
      const halfWidth = text.width / 2;
      const halfHeight = text.height / 2;

      // Simple bounding box check (doesn't account for rotation)
      const minX = text.x - halfWidth - tolerance;
      const maxX = text.x + halfWidth + tolerance;
      const minY = text.y - halfHeight - tolerance;
      const maxY = text.y + halfHeight + tolerance;

      if (worldX >= minX && worldX <= maxX && worldY >= minY && worldY <= maxY) {
        return text;
      }
    }

    return null;
  }

  /**
   * Get the maximum z-index of all texts
   */
  getMaxZIndex(): number {
    let maxZ = 0;
    for (const text of this.texts.values()) {
      maxZ = Math.max(maxZ, text.zIndex || 0);
    }
    return maxZ;
  }

  /**
   * Schedule a debounced save
   */
  private scheduleSave(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    if (this.autoSaveEnabled) {
      this.saveTimeout = setTimeout(() => {
        this.saveDirtyTexts();
      }, TextManager.SAVE_DELAY);
    }
  }

  /**
   * Save all pending changes
   */
  async saveDirtyTexts(): Promise<void> {
    if (this.dirtyTexts.size === 0 && this.newTexts.size === 0 && this.deletedTexts.size === 0) return;

    // Run before-save callback (e.g., to save layers first and update local IDs)
    if (this.onBeforeSave) {
      await this.onBeforeSave();
    }

    this.isSaving = true;
    this.notifySaveStateChanged();

    try {
      // 1. Delete texts marked for deletion
      for (const textId of this.deletedTexts) {
        try {
          await textsApi.delete(this.mapId, textId);
        } catch (err) {
          console.error(`Failed to delete text ${textId}:`, err);
        }
      }
      this.deletedTexts.clear();

      // 2. Create new texts
      const idMapping = new Map<string, string>();
      for (const localId of this.newTexts) {
        const text = this.texts.get(localId);
        if (text) {
          try {
            const { id, regionX, regionY, ...textData } = text;
            const response = await textsApi.create(this.mapId, textData as Omit<TextInstance, 'id' | 'regionX' | 'regionY'>);

            this.texts.delete(localId);
            const serverText = { ...response.text, regionX: response.regionX, regionY: response.regionY };
            this.texts.set(serverText.id, serverText);
            idMapping.set(localId, serverText.id);
          } catch (err) {
            console.error(`Failed to create text:`, err);
          }
        }
      }
      this.newTexts.clear();

      // 3. Update dirty texts
      for (const textId of this.dirtyTexts) {
        const text = this.texts.get(textId);
        if (text) {
          try {
            await textsApi.update(this.mapId, text.id, text);
          } catch (err) {
            console.error(`Failed to update text ${text.id}:`, err);
          }
        }
      }
      this.dirtyTexts.clear();

      this.notifyTextsChanged();
    } catch (err) {
      console.error('Failed to save texts:', err);
    } finally {
      this.isSaving = false;
      this.notifySaveStateChanged();
    }
  }

  /**
   * Check if there are unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.dirtyTexts.size > 0 || this.newTexts.size > 0 || this.deletedTexts.size > 0;
  }

  /**
   * Notify that texts have changed
   */
  private notifyTextsChanged(): void {
    this.onTextsChanged?.();
  }

  /**
   * Update layer IDs when layers get new server IDs
   * Called when LayerManager saves new layers and gets back server IDs
   */
  updateLayerIds(idMapping: Map<string, string>): void {
    let changed = false;
    for (const text of this.texts.values()) {
      if (text.layerId && idMapping.has(text.layerId)) {
        text.layerId = idMapping.get(text.layerId)!;
        this.dirtyTexts.add(text.id);
        changed = true;
      }
    }
    if (changed) {
      this.scheduleSave();
      this.notifyTextsChanged();
      this.notifySaveStateChanged();
    }
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }
}
